<template>
  <div class="tutorial-list">
    <div
      v-if="heading"
      class="text-caption font-weight-bold text-uppercase mb-1"
    >
      {{ heading }}
    </div>
    <div
      v-for="video in videos"
      :key="video.id"
      class="tutorial-list__item mb-3"
      :data-cy="`tutorial-${video.id}`"
    >
      <v-btn
        v-if="video.external"
        :href="tutorialSrc(video, hebrew)"
        target="_blank"
        rel="noopener"
        variant="tonal"
        color="orange"
        block
        prepend-icon="mdi-youtube"
        @click="trackTutorialEvent('tutorial_open', { videoId: video.id })"
      >
        {{ hebrew ? video.titleHe : video.title }}
      </v-btn>
      <template v-else>
        <div v-if="videos.length > 1" class="text-body-2 font-weight-medium mb-1">
          {{ hebrew ? video.titleHe : video.title }}
        </div>
        <video
          :key="tutorialSrc(video, hebrew)"
          class="tutorial-list__video"
          :src="tutorialSrc(video, hebrew)"
          controls
          playsinline
          preload="metadata"
          @timeupdate="stopAtEndCard($event, video)"
          @play="restartIfAtEnd($event, video)"
        ></video>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  tutorialEndAt,
  tutorialSrc,
  type TutorialVideo,
} from "../config/tutorials";
import { trackTutorialEvent } from "../helpers/tutorialAnalytics";

const props = defineProps<{
  videos: TutorialVideo[];
  heading?: string;
  hebrew?: boolean;
}>();

function endAt(video: TutorialVideo): number | undefined {
  return tutorialEndAt(video, !!props.hebrew);
}

/**
 * Local clips carry a marketing end card. Pause on the last board frame so
 * the in-app viewer never sees it, while the same file stays usable on
 * YouTube and in ads.
 */
function stopAtEndCard(event: Event, video: TutorialVideo) {
  const stop = endAt(video);
  if (stop == null) return;
  const el = event.target as HTMLVideoElement;
  if (el.currentTime >= stop) {
    el.pause();
    if (el.currentTime > stop) el.currentTime = stop;
  }
}

/** Pressing play on the trimmed end starts the clip over instead of sticking. */
function restartIfAtEnd(event: Event, video: TutorialVideo) {
  const stop = endAt(video);
  if (stop == null) return;
  const el = event.target as HTMLVideoElement;
  if (el.currentTime >= stop - 0.25) {
    el.currentTime = 0;
  }
}
</script>

<style scoped>
.tutorial-list__video {
  display: block;
  width: 100%;
  border-radius: 8px;
  background: #000;
}
</style>
